// Pure passive-integer fold preparation shared by BASE, PL, status removal and hunger.
import { refoldPassiveIntegers, heldPassiveEntries, CPI_JUMP_FAMILY, CPI_CAPACITY_FAMILY } from '../compile/passive-integer-fold';
import type { PassiveIntegerFoldDependencies } from '../compile/passive-integer-fold';
import { C1_CONTRACT } from '../compile/possessor-compile';
import { createFormulaReadContext } from '../compile/formula-read-context';
import { compileCreatureSpeeds } from '../compile/creature-speeds';
import { resolveCreatureShapeshiftForm } from '../compile/creature-shapeshift-form';
import { readInventoryField, readItemUuidSlot, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import type { DefinitionStore } from '../store/definition-store';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => typeof v === 'string' ? v.toUpperCase() : '';
const validUuid = (v: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
/** The C1 reader deps over a definition lookup (the same shape compilePossessor's readerDeps builds). */
export function cpiFoldDeps(definition: (id: string) => Row | null, missingDefinitionIds: readonly string[] = []): PassiveIntegerFoldDependencies {
    const def = (id: unknown): Row | null => typeof id === 'string' && validUuid(id) ? definition(id.toUpperCase()) : null;
    const c = C1_CONTRACT;
    const base = {
        catalogRows: c.catalogRows, abilityTypes: c.abilityTypes,
        aggregationStrategy: (name: string) => c.catalogRows[name]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: (id: string) => def(id) as never,
        resolveInventoryInstance: () => null,
        resolveItemName: (id: string) => { const d = def(id); return d && isObj(d.items) ? (d.items as Row).item_name ?? null : null; },
        queryFullItem: (id: string) => def(id) as never,
        queryTemplateDefinition: (id: string) => def(id) as never,
        metadata: c.rollup,
    };
    // family 8 (the speed shield): the browser speed compile, as the C1 status lane runs it — a condition the heal cannot answer (light, the
    // scene) refuses the compile, so the shield is simply not folded for that row
    const speedDeps = { ...base, ...c.rollup, metadata: c.rollup, skillEntries: c.skillEntries, inventoryAbilityTypes: c.rollup.inventoryAbilityTypes,
        equipmentSlots: c.equipmentSlots,
        conditionSource: (kind: string): never => { throw new Error('unsupported-condition:' + kind); },
        queryItemType: (id: string) => { const d = def(id); return d && isObj(d.items) ? (d.items as Row).item_type ?? '' : ''; } };
    // a NON-PC row's passive speed share is the C-PI speed family's (compute_speeds never sees it — the C1 cpiSpeedView twin)
    const speedView = (host: Row): Row => {
        if (isObj(host.player_characters)) return host;
        const h = { ...host }; delete h.passive_ability;
        const comp = isObj(h.computed) ? { ...(h.computed as Row) } : {}; const cr = isObj(comp.creature) ? { ...(comp.creature as Row) } : {};
        if (isObj(cr.abilities)) { const ab = { ...(cr.abilities as Row) }; delete ab.passive_ability; cr.abilities = ab; }
        comp.creature = cr; h.computed = comp; return h;
    };
    return { ...base, missingDefinitionIds,
        compileSpeeds: (host: Row, shield: boolean) => {
            const view = speedView(host), selected = resolveCreatureShapeshiftForm(view, speedDeps as never);
            return (selected.ok ? compileCreatureSpeeds(view, speedDeps as never, { shield, form: selected.form }) : selected) as never;
        },
    } as unknown as PassiveIntegerFoldDependencies;
}

/** Every definition id the fold of this row reads: its held passives. */
export function cpiDefinitionIds(row: Row): { passives: string[]; all: string[]; optional: string[] } {
    const ctx = createFormulaReadContext(cpiFoldDeps(() => null) as never);
    const passives = new Set<string>();
    for (const e of heldPassiveEntries(row, ctx)) {
        const id = upper(isObj(e) ? (e.ability_id ?? e.item_id ?? (typeof e.item_arr === 'string' ? e.item_arr : '')) : readLayerListEntryUuid(e) || e);
        if (validUuid(id)) passives.add(id);
    }
    const all = new Set(passives);
    // family 6 (jump) also reads the row's statuses, equipped templates and feats — OPTIONAL: a missing one leaves only jump unfolded
    const optional = new Set<string>();
    const st = isObj(row.statuses) && Array.isArray((row.statuses as Row).target) ? ((row.statuses as Row).target as unknown[]).filter(isObj) : [];
    for (const s of st) { const id = upper(s.status_item_id); if (validUuid(id)) optional.add(id); }
    const walk = (list: unknown): void => {
        for (const inst of (Array.isArray(list) ? list : isObj(list) ? Object.values(list) : []).filter(isObj)) {
            if (readItemUuidSlot(readInventoryField(inst as never, 'equipped') ?? '') !== '') { const t = upper(readInventoryField(inst as never, 'inventory_item_id')); if (validUuid(t)) optional.add(t); }
            walk(inst.inventory_in_container);
        }
    };
    walk(row.inventory);
    const featLists = [isObj(row.item_layer_types) ? (row.item_layer_types as Row).feat : undefined, isObj(row.computed) && isObj((row.computed as Row).creature) && isObj(((row.computed as Row).creature as Row).abilities) ? (((row.computed as Row).creature as Row).abilities as Row).feat : undefined];
    for (const l of featLists) for (const e of Array.isArray(l) ? l : []) { const id = upper(isObj(e) ? e.item_id : e); if (validUuid(id)) optional.add(id); }
    for (const id of passives) optional.delete(id);
    return { passives: [...passives], all: [...all], optional: [...optional] };
}

type CpiFolded = ReturnType<typeof refoldPassiveIntegers>;
type CpiRefusal = ReturnType<DefinitionStore['refusal']>;
/** Discover dynamic carrier misses with the ordinary readers, then exclude only store-confirmed deletions. */
function refoldCpi(row: Row, definition: (id: string) => Row | null, refusal: (id: string) => CpiRefusal): CpiFolded {
    const missingDefinitionIds = new Set<string>();
    const lookup = (id: string): Row | null => {
        if (refusal(id) === 'missing') missingDefinitionIds.add(upper(id));
        return definition(id);
    };
    for (const id of cpiDefinitionIds(row).optional) if (refusal(id) === 'missing') missingDefinitionIds.add(id);
    const knownMissing = missingDefinitionIds.size;
    const folded = refoldPassiveIntegers(row, cpiFoldDeps(lookup, [...missingDefinitionIds]));
    return missingDefinitionIds.size > knownMissing ? refoldPassiveIntegers(row, cpiFoldDeps(definition, [...missingDefinitionIds])) : folded;
}
/** The ids a fold READ — what a claim must bind: the held passives, the passive ids the collection walked, and the jump / capacity source
 *  hrefs when those families folded. Used by the PL pricer (pl/pl-claim.ts) to bind its definition reads. */
export function cpiFoldReadIds(ids: { passives: string[] }, folded: CpiFolded): string[] {
    const cpi = isObj(folded.creature.cpi) ? folded.creature.cpi as Row : {};
    const jumpIds = CPI_JUMP_FAMILY in cpi ? (folded.collection.jump?.rows ?? []).map(r => upper(r.href_id)).filter(validUuid) : [];
    const capacityIds = !folded.collection.capacity?.incomplete && CPI_CAPACITY_FAMILY in cpi
        ? (folded.collection.capacity?.sources ?? []).filter(r => r.kind !== 'host').map(r => upper(r.href_id)).filter(validUuid) : [];
    return [...new Set([...ids.passives, ...folded.collection.passiveIds, ...jumpIds, ...capacityIds])];
}

/** One step of a STRICT fold preparation (the PL pricer's — PL reads the folded values, so it prices the folded row, never the row as
 *  served): the ids still to fetch (every referenced definition: passives, statuses, equipped templates, feats, and whatever the jump /
 *  capacity resolvers report missing — none silently skipped because it has not loaded yet), the REQUIRED passives the store refused, or
 *  the fold once everything it references is held or refused. Confirmed deletions author nothing; incomplete capacity keeps its served
 *  value while the other families fold. Pure over the given lookups; the caller fetches and calls again. */
export interface CpiFoldPrep { fetch: string[]; refused: string[]; folded: CpiFolded | null; readIds: string[] }
export function prepareCpiFold(row: Row, definition: (id: string) => Row | null, isRefused: (id: string) => CpiRefusal): CpiFoldPrep {
    const none = { fetch: [] as string[], refused: [] as string[], folded: null, readIds: [] as string[] };
    const ids = cpiDefinitionIds(row);
    const refused = ids.passives.filter(id => !definition(id) && isRefused(id));
    if (refused.length) return { ...none, refused };
    const fetch = [...ids.all, ...ids.optional].filter(id => !definition(id) && !isRefused(id));
    if (fetch.length) return { ...none, fetch };
    const folded = refoldCpi(row, definition, isRefused);
    const dynamic = [...new Set([...(folded.collection.jump?.missing ?? []), ...(folded.collection.capacity?.missing ?? [])].map(upper))].filter(id => !definition(id) && !isRefused(id));
    if (dynamic.length) return { ...none, fetch: dynamic };
    return { ...none, folded, readIds: cpiFoldReadIds(ids, folded) };
}

/** Apply only folded cells to the held row (null unsets; emptied maps are removed). */
export function applyCpiCells(row: Row, cells: Record<string, unknown>): void {
    for (const [path, value] of Object.entries(cells)) {
        const keys = path.split('.');
        const last = keys.pop()!;
        let node: Row = row;
        let ok = true;
        for (const k of keys) {
            if (!isObj(node[k])) { if (value === null) { ok = false; break; } node[k] = {}; }
            node = node[k] as Row;
        }
        if (!ok) continue;
        if (value === null) delete node[last]; else node[last] = JSON.parse(JSON.stringify(value));
    }
    const creature = isObj(row.computed) && isObj((row.computed as Row).creature) ? (row.computed as Row).creature as Row : null;
    if (creature) for (const k of ['modifiers', 'modifier_sources', 'cpi', 'meta']) if (isObj(creature[k]) && !Object.keys(creature[k] as Row).length) delete creature[k];
}
